import React, { useMemo, useState } from 'react';
import { compareAcademyTeams } from '../utils/academyTeamSort';

const teamId = (team) => Number(team.team_id);

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const teamUsage = (team) => {
  const hasPlayers = team.player_count != null && team.player_count !== '';
  const hasParents = team.parent_count != null && team.parent_count !== '';
  if (!hasPlayers && !hasParents) {
    return null;
  }
  const players = Number(team.player_count) || 0;
  const parents = Number(team.parent_count) || 0;
  const parts = [];
  if (hasPlayers) {
    parts.push(countLabel(players, 'Player', 'Players'));
  }
  if (hasParents) {
    parts.push(countLabel(parents, 'Parent', 'Parents'));
  }
  return {
    active: players > 0 || parents > 0,
    label: parts.join(', '),
  };
};

const AcademyTeamPicker = ({ teams, selectedTeamIds, onToggle, hint }) => {
  const [teamSearch, setTeamSearch] = useState('');
  const selectedSet = useMemo(
    () => new Set((selectedTeamIds || []).map(Number).filter((id) => id > 0)),
    [selectedTeamIds]
  );

  const uniqueTeams = useMemo(() => {
    const unique = [];
    const seen = new Set();
    (teams || []).forEach((team) => {
      const id = teamId(team);
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      unique.push(team);
    });
    return unique;
  }, [teams]);

  const assignedTeams = useMemo(
    () => uniqueTeams
      .filter((team) => selectedSet.has(teamId(team)))
      .sort(compareAcademyTeams),
    [uniqueTeams, selectedSet]
  );

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    const list = q
      ? uniqueTeams.filter((team) =>
        `${team.team_name} ${team.age_group || ''}`.toLowerCase().includes(q)
      )
      : uniqueTeams.slice();
    return list.sort((a, b) => {
      const aSel = selectedSet.has(teamId(a)) ? 0 : 1;
      const bSel = selectedSet.has(teamId(b)) ? 0 : 1;
      if (aSel !== bSel) {
        return aSel - bSel;
      }
      return String(a.team_name || '').localeCompare(String(b.team_name || ''));
    });
  }, [uniqueTeams, teamSearch, selectedSet]);

  return (
    <div className="team-picker">
      {hint ? <p className="hint">{hint}</p> : null}
      <p className="hint">
        {assignedTeams.length === 0
          ? 'No teams assigned yet.'
          : `${assignedTeams.length} team${assignedTeams.length === 1 ? '' : 's'} assigned`}
      </p>
      {assignedTeams.length > 0 && (
        <ul className="assigned-teams">
          {assignedTeams.map((team) => {
            const usage = teamUsage(team);
            return (
            <li key={`assigned-${team.team_id}`}>
              <span>
                {team.team_name}
                {team.age_group ? <small> · {team.age_group}</small> : null}
                {usage ? <small className="team-usage">{usage.label}</small> : null}
              </span>
              <button type="button" className="clear-person" onClick={() => onToggle(teamId(team))}>
                Remove
              </button>
            </li>
            );
          })}
        </ul>
      )}
      <label className="field team-search">
        Search teams
        <input
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          placeholder="U11, Academy United…"
        />
      </label>
      <div className="team-list">
        {filteredTeams.length === 0 ? (
          <p className="hint">No matching teams.</p>
        ) : (
          filteredTeams.map((team) => {
            const id = teamId(team);
            const assigned = selectedSet.has(id);
            const usage = teamUsage(team);
            return (
              <label key={team.team_id} className={`team-option${assigned ? ' is-assigned' : ''}${usage?.active ? ' is-active' : ''}`}>
                <input
                  type="checkbox"
                  checked={assigned}
                  onChange={() => onToggle(id)}
                />
                <span className="team-option-label">
                  <span className="team-option-name">
                    {team.team_name}
                    {team.age_group ? <small> · {team.age_group}</small> : null}
                  </span>
                  {usage ? <span className="team-usage">{usage.label}</span> : null}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AcademyTeamPicker;
